import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const SalesTotalCountSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const OPPORTUNITY_STAGES = [
  "Contacts",
  "Leads",
  "Opportunity",
  "Prospect",
  "Proposal In Progress",
  "Proposal Submission",
  "Negotiation in Progress",
  "Contract Won",
  "Lost",
  "Hold",
];
const SERVICE_OFFERINGS = [
  "Procure to Pay",
  "e-Sourcing",
  "Procurement Services",
  "Tail Spend Management",
  "SPOT Buys",
  "Inventory Management",
  "Stores Management",
  "Non Moving inventory Liquidation",
  "Vendor Managed Inventory",
  "Master data Management",
];

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractSalesPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { sales: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      sales: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.sales || dataBlock.saleList || dataBlock.items || root.sales;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.sales || inner.saleList || inner.items;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.sales == null &&
    dataBlock.saleList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null || dataBlock.companyName != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return {
    sales: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? pagination
        : {},
  };
}

const SalesTracker = () => {
  const navigate = useNavigate();
  const { canCreate, canView, canUpdate, canDelete, user } =
    useModuleAccess("sales_tracker");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("salesTrackerFilters") || "{}",
  );
  const userRole = user?.role || localStorage.getItem("role");

  const [sales, setSales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteSaleId, setDeleteSaleId] = useState(null);
  const [deleteSaleName, setDeleteSaleName] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [selectedOpportunityStage, setSelectedOpportunityStage] = useState(
    savedFilters.selectedOpportunityStage || "",
  );
  const [selectedServiceOffering, setSelectedServiceOffering] = useState(
    savedFilters.selectedServiceOffering || "",
  );
  const [fromDate, setFromDate] = useState(savedFilters.fromDate || "");
  const [toDate, setToDate] = useState(savedFilters.toDate || "");
  const [exportMode, setExportMode] = useState(
    savedFilters.exportMode || "all",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [salesTotalCount, setSalesTotalCount] = useState(0);
  const [isSalesCountLoading, setIsSalesCountLoading] = useState(true);

  const fetchSales = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-sales",
        page: currentPage,
        limit: itemsPerPage,
        search: activeSearchQuery ? activeSearchQuery.trim() : "",
        sortBy,
        sortOrder,
      };
      if (selectedOpportunityStage) {
        payload.opportunityStage = selectedOpportunityStage;
      }
      if (selectedServiceOffering) {
        payload.serviceOffering = selectedServiceOffering;
        payload.serviceOfferings = [selectedServiceOffering];
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const { sales: list, pagination } = extractSalesPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setSales(list);
        const total =
          typeof pagination.total === "number" ? pagination.total : list.length;
        setTotalCount(total);
      } else {
        toast.error("Failed to fetch sales records", { autoClose: 2000 });
        setSales([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch sales error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while fetching sales records";
      toast.error(msg, { autoClose: 2000 });
      setSales([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    selectedOpportunityStage,
    selectedServiceOffering,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = sales.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const fetchSalesTotalCount = async () => {
    try {
      setIsSalesCountLoading(true);

      const payload = {
        action: "sales-counts",
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      if (responseData.success) {
        setSalesTotalCount(responseData.totalCount);
      }
    } catch (error) {
      console.error("Sales total count error:", error);
      setSalesTotalCount(0);
    } finally {
      setIsSalesCountLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesTotalCount();
  }, []);

  const columnMap = {
    companyName: "companyName",
    contactPerson: "contactPerson",
    email: "email",
    contactNumber: "contactNumber",
    priority: "priority",
    opportunityStage: "opportunityStage",
    serviceOffering: "serviceOffering",
    createdAt: "createdAt",
    createdBy: "createdBy",
  };

  const handleSort = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em" }}
      />
    );
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      selectedOpportunityStage,
      selectedServiceOffering,
      fromDate,
      toDate,
      exportMode,
      currentPage,
      itemsPerPage,
    };
    sessionStorage.setItem("salesTrackerFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    selectedOpportunityStage,
    selectedServiceOffering,
    fromDate,
    toDate,
    exportMode,
    currentPage,
    itemsPerPage,
    sortBy, // ← add
    sortOrder,
  ]);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleDeleteClick = (id, name) => {
    setDeleteSaleId(id);
    setDeleteSaleName(name || "");
  };

  const handleDelete = async () => {
    if (!deleteSaleId) return;

    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setIsDeleting(true);
    try {
      const payload = {
        action: "delete-sales",
        id: deleteSaleId,
        requestUserId,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData.message || "Sales record deleted successfully",
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modaldeleteSales").modal("hide");
        }
        setDeleteSaleId(null);
        setDeleteSaleName("");
        if (sales.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchSales();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete sales record", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete sales error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while deleting sales record";
      toast.error(msg, { autoClose: 2000 });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExport = async () => {
    if (exportMode === "date") {
      if (!fromDate || !toDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(fromDate) > new Date(toDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);
    try {
      const payload = {
        action: "export-sales",
      };

      if (exportMode === "date") {
        payload.fromDate = fromDate;
        payload.toDate = toDate;
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) payload.search = trimmedSearch;

      if (selectedOpportunityStage)
        payload.opportunityStage = selectedOpportunityStage;
      if (selectedServiceOffering)
        payload.serviceOfferings = [selectedServiceOffering];
      if (selectedServiceOffering)
        payload.serviceOffering = selectedServiceOffering;

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      let base64 = "";
      if (typeof response?.data?.body === "string") {
        base64 = response.data.body;
      } else if (typeof response?.data === "string") {
        base64 = response.data;
      } else if (typeof response?.data?.data === "string") {
        base64 = response.data.data;
      }

      if (!base64 || !base64.startsWith("UEs")) {
        throw new Error("Invalid Excel data received");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      link.href = url;
      link.download = `sales-export-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Export completed successfully", { autoClose: 2000 });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Sales Tracker</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/salesTrackerAdd" className="btn btn-blue mr-2">
                  Add New
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isSalesCountLoading ? (
                  <SalesTotalCountSkeleton />
                ) : (
                  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                    <div className="card supplier-stat-card">
                      <div className="card-body">
                        <div
                          className="stat-title"
                          style={{
                            color: "#127aea",
                            fontSize: "13px",
                            fontWeight: "600",
                            marginBottom: "8px",
                          }}
                        >
                          Total Sales
                        </div>

                        <div
                          className="stat-value"
                          style={{
                            color: "#2c7fd8",
                            fontSize: "22px",
                            fontWeight: "700",
                          }}
                        >
                          {salesTotalCount}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-4 mb-2 mb-md-0">
                      <div className="form-group mb-0">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search sales..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3 mb-2 mb-md-0">
                      <div className="form-group mb-0">
                        <select
                          className="form-control"
                          value={selectedOpportunityStage}
                          onChange={(e) => {
                            setSelectedOpportunityStage(e.target.value);
                            if (currentPage !== 1) setCurrentPage(1);
                          }}
                        >
                          <option value="">Select Stage</option>
                          {OPPORTUNITY_STAGES.map((stage) => (
                            <option key={stage} value={stage}>
                              {stage}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3 mb-2 mb-md-0">
                      <div className="form-group mb-0">
                        <select
                          className="form-control"
                          value={selectedServiceOffering}
                          onChange={(e) => {
                            setSelectedServiceOffering(e.target.value);
                            if (currentPage !== 1) setCurrentPage(1);
                          }}
                        >
                          <option value="">Select Service</option>
                          {SERVICE_OFFERINGS.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-2 text-md-right">
                      <div className="form-group mb-0">
                        <p className="mb-0">
                          {(activeSearchQuery ||
                            selectedOpportunityStage ||
                            selectedServiceOffering) && (
                            <span className="ml-2 text-muted">
                              (Showing: {sales.length})
                            </span>
                          )}
                        </p>
                        {(selectedOpportunityStage ||
                          selectedServiceOffering) && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 mt-1"
                            onClick={() => {
                              setSelectedOpportunityStage("");
                              setSelectedServiceOffering("");
                              if (currentPage !== 1) setCurrentPage(1);
                            }}
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row align-items-center mt-2">
                    <div className="col-md-12 text-md-right">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => setShowExportModal(true)}
                      >
                        <i className="fa-solid fa-file-export mr-1" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : sales.length === 0 && !activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  There are no sales records yet.
                  <br />
                  <div className="mt-3">
                    {canCreate && (
                      <Link to="/salesTrackerAdd" className="btn btn-blue">
                        Add New Sales
                      </Link>
                    )}
                  </div>
                </div>
              ) : sales.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No sales records found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="sales"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          <th
                            onClick={() => handleSort("companyName")}
                            style={{ whiteSpace: "nowrap", minWidth: "140px" }}
                          >
                            Company {getSortIcon("companyName")}
                          </th>
                          <th
                            onClick={() => handleSort("contactPerson")}
                            style={{ minWidth: "100px" }}
                          >
                            Contact {getSortIcon("contactPerson")}
                          </th>
                          <th
                            onClick={() => handleSort("email")}
                            style={{
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "150px",
                            }}
                          >
                            Email {getSortIcon("email")}
                          </th>
                          <th style={{ minWidth: "90px" }}>Phone</th>
                          <th style={{ minWidth: "70px" }}>Priority</th>
                          <th
                            onClick={() => handleSort("opportunityStage")}
                            style={{ minWidth: "100px" }}
                          >
                            Stage {getSortIcon("opportunityStage")}
                          </th>
                          <th style={{ minWidth: "100px", maxWidth: "140px" }}>
                            Service
                          </th>
                          <th
                            onClick={() => handleSort("createdBy")}
                            style={{ whiteSpace: "nowrap", minWidth: "120px" }}
                          >
                            Created By {getSortIcon("createdBy")}
                          </th>
                          {userRole === "admin" && (
                            <th
                              style={{ whiteSpace: "nowrap", minWidth: "70px" }}
                            >
                              Status
                            </th>
                          )}
                          <th style={{ minWidth: "100px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sales.map((row, index) => {
                          const sid = row._id || row.id;
                          const createdName =
                            row.createdBy && typeof row.createdBy === "object"
                              ? row.createdBy.name
                              : row.createdBy || "-";
                          return (
                            <tr key={sid}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td>{row.companyName || "-"}</td>
                              <td>
                                <div
                                  style={{
                                    maxWidth: "120px",
                                    wordWrap: "break-word",
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {row.contactPerson || "-"}
                                </div>
                              </td>
                              <td
                                style={{
                                  maxWidth: "250px",
                                  width: "250px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={row.email || "-"}
                              >
                                {row.email || "-"}
                              </td>
                              <td>{row.contactNumber || "-"}</td>
                              <td>{row.priority || "-"}</td>
                              <td>{row.opportunityStage || "-"}</td>
                              <td>
                                <div
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    maxWidth: "140px",
                                  }}
                                >
                                  {(Array.isArray(row.serviceOffering)
                                    ? row.serviceOffering
                                        .filter(Boolean)
                                        .join(", ")
                                    : row.serviceOffering || "") || "-"}
                                </div>
                              </td>
                              <td>{createdName}</td>
                              {userRole === "admin" && (
                                <td>
                                  <span
                                    className={`fwt_600 ${
                                      row.status === "active"
                                        ? "f_green"
                                        : "f_red"
                                    }`}
                                    style={{ fontSize: "90%", fontWeight: 550 }}
                                  >
                                    {row.status
                                      ? row.status.charAt(0).toUpperCase() +
                                        row.status.slice(1).toLowerCase()
                                      : "-"}
                                  </span>
                                </td>
                              )}
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {canView && (
                                    <Link
                                      to={`/salesTrackerView/${sid}`}
                                      className="crsr_pntr"
                                      style={{ display: "inline-block" }}
                                    >
                                      <i className="fa-solid fa-eye mr-2" />
                                    </Link>
                                  )}
                                  {canUpdate && (
                                    <Link
                                      to={`/salesTrackerEdit/${sid}`}
                                      className="crsr_pntr"
                                      style={{ display: "inline-block" }}
                                    >
                                      <i className="fa-solid fa-pen mr-2" />
                                    </Link>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      className="crsr_pntr btn btn-link p-0 border-0 align-baseline"
                                      data-toggle="modal"
                                      data-target="#modaldeleteSales"
                                      onClick={() =>
                                        handleDeleteClick(sid, row.companyName)
                                      }
                                      title="Delete"
                                      style={{
                                        display: "inline-block",
                                        color: "var(--blue, #0094a9)",
                                        textDecoration: "none",
                                      }}
                                    >
                                      <i
                                        className="fa-solid fa-trash-can"
                                        style={{ color: "inherit" }}
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && sales.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldeleteSales"
        tabIndex={-1}
        aria-labelledby="modaldeleteSalesLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteSalesLabel">
                Delete Sales Record
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteSaleName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportSalesLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportSalesLabel">
                Export Sales Data
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllSales"
                    name="exportTypeSales"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllSales"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateSales"
                    name="exportTypeSales"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateSales"
                  >
                    Filter by Date
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default SalesTracker;
